import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import EmptyState from '../../components/EmptyState';
import { Plug, Plus, Loader2, PlugZap, Link2, RefreshCw, ShieldCheck, X, Trash2, CheckCircle2, AlertTriangle, Upload, FileUp, ChevronDown, ChevronRight, Layers, Ban } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const P6_STATUS_STYLE = {
  CREATE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  UPDATE: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  UNCHANGED: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  EXTERNAL_MISSING: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  BLOCKED: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  CONFLICT: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300',
  WARNING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  ERROR: 'bg-red-200 text-red-800 dark:bg-red-950 dark:text-red-300',
};
const P6_STATUS_ORDER = ['CREATE', 'UPDATE', 'UNCHANGED', 'EXTERNAL_MISSING', 'BLOCKED', 'CONFLICT', 'WARNING', 'ERROR'];
const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const IntegrationHub = () => {
  const [connections, setConnections] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ connectorType: 'mock', displayName: '', apiKey: '' });
  const [sel, setSel] = useState(null);
  const [extProjects, setExtProjects] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  // Phase 10B-1 — P6 XER preview (PREVIEW ONLY, no apply)
  const [p6ProjectId, setP6ProjectId] = useState('');
  const [p6File, setP6File] = useState(null);
  const [p6Preview, setP6Preview] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [p6Confirm, setP6Confirm] = useState(false);
  const [p6Result, setP6Result] = useState(null);
  const [p6Imports, setP6Imports] = useState([]);
  const [p6Baselines, setP6Baselines] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, cat, pr] = await Promise.all([
        axios.get(`${API}/integrations/connections`),
        axios.get(`${API}/integrations/catalog`),
        axios.get(`${API}/admin/projects`),
      ]);
      setConnections(c.data); setCatalog(cat.data); setProjects(pr.data);
    } catch { toast.error('Failed to load integration hub'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/integrations/connections`, {
        connectorType: form.connectorType, displayName: form.displayName || form.connectorType,
        secret: form.apiKey ? { apiKey: form.apiKey } : null,
      });
      toast.success('Connection created');
      setShowCreate(false); setForm({ connectorType: 'mock', displayName: '', apiKey: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const openConn = async (c) => {
    setSel(c); setJob(null); setExtProjects([]);
    setP6Preview(null); setP6File(null); setP6ProjectId(''); setExpanded({});
    setP6Confirm(false); setP6Result(null); setP6Baselines([]); setP6Imports([]);
    if (c.connectorType === 'p6') { loadP6Meta(c.id); }
    try {
      const m = await axios.get(`${API}/integrations/connections/${c.id}/mappings`);
      setMappings(m.data);
    } catch { setMappings([]); }
  };
  const testConn = async () => {
    try { const r = await axios.post(`${API}/integrations/connections/${sel.id}/test`); toast.success(r.data.ok ? 'Connection healthy' : 'Test returned not-ok'); }
    catch { toast.error('Test failed'); }
  };
  const discover = async () => {
    try { setExtProjects((await axios.get(`${API}/integrations/connections/${sel.id}/discover`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Discover failed'); }
  };
  const mapProject = async (extId, rvxId) => {
    if (!rvxId) return;
    try { await axios.post(`${API}/integrations/connections/${sel.id}/mappings`, { externalProjectId: extId, rvxProjectId: rvxId }); toast.success('Mapped'); setMappings((await axios.get(`${API}/integrations/connections/${sel.id}/mappings`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Map failed'); }
  };
  const runSync = async (extId) => {
    setBusy(true);
    try { setJob((await axios.post(`${API}/integrations/connections/${sel.id}/sync`, { externalProjectId: extId })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Sync preview failed'); }
    finally { setBusy(false); }
  };
  const applyJob = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/integrations/sync-jobs/${job.id}/apply`); toast.success(`Applied ${r.data.applied}, blocked ${r.data.blocked}`); setJob({ ...job, state: r.data.state }); }
    catch { toast.error('Apply failed'); }
    finally { setBusy(false); }
  };
  const uploadP6Preview = async () => {
    if (!p6File) { toast.error('Choose an XER file first'); return; }
    if (!p6ProjectId) { toast.error('Select an RVX project to diff against'); return; }
    setBusy(true); setP6Preview(null); setExpanded({});
    try {
      const fd = new FormData();
      fd.append('file', p6File);
      fd.append('rvxProjectId', p6ProjectId);
      const r = await axios.post(`${API}/integrations/connections/${sel.id}/p6/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setP6Preview(r.data);
      if (r.data.needsProjectSelection) toast.message('XER has multiple projects — select one to continue');
      else toast.success('Preview generated (no data applied)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const loadP6Meta = async (connId) => {
    try {
      const [imp, bl] = await Promise.all([
        axios.get(`${API}/integrations/connections/${connId}/p6/imports`),
        axios.get(`${API}/integrations/connections/${connId}/p6/baselines`),
      ]);
      setP6Imports(imp.data); setP6Baselines(bl.data);
    } catch { /* non-fatal */ }
  };

  const applyP6 = async (mode) => {
    if (!p6Preview?.id) return;
    setBusy(true); setP6Confirm(false);
    try {
      const r = await axios.post(`${API}/integrations/connections/${sel.id}/p6/preview-jobs/${p6Preview.id}/apply`, { confirm: true, applyMode: mode });
      setP6Result(r.data);
      toast.success(mode === 'BASELINE' ? 'Baseline imported (immutable)' : `Applied — ${r.data.status}`);
      loadP6Meta(sel.id);
    } catch (e) {
      const d = e.response?.data?.detail || 'Apply failed';
      if (String(d).includes('PREVIEW_STALE')) toast.error('Preview is stale — please re-upload and preview again');
      else toast.error(d);
    } finally { setBusy(false); }
  };

  const selectBaseline = async (bid) => {
    try { await axios.post(`${API}/integrations/p6/baselines/${bid}/select`); toast.success('Baseline selected'); loadP6Meta(sel.id); }
    catch { toast.error('Select failed'); }
  };

  const del = async (c) => {    if (!window.confirm('Delete connection? Secret is destroyed; governed RVX records are untouched.')) return;
    try { await axios.delete(`${API}/integrations/connections/${c.id}`); toast.success('Deleted'); setSel(null); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <Layout><LoadingState message="Loading Integration Hub..." /></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="integration-hub-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><Plug className="w-7 h-7 text-cyan-600" />Integration Hub</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Securely connect enterprise systems. Inbound-first, governed — imports never overwrite RVX decisions.</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 shadow-md" data-testid="create-connection-button"><Plus className="w-5 h-5" />New Connection</button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Connections list */}
          <div className="lg:col-span-1 space-y-3" data-testid="connections-list">
            {connections.length === 0 ? (
              <EmptyState icon={Plug} title="No connections" description="Create a connection to an enterprise system." />
            ) : connections.map((c) => (
              <button key={c.id} onClick={() => openConn(c)} className={`w-full text-left bg-white dark:bg-slate-900 rounded-xl p-4 border transition-shadow ${sel?.id === c.id ? 'border-cyan-500 shadow-md' : 'border-slate-200 dark:border-slate-800 hover:shadow-sm'}`} data-testid={`connection-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900 dark:text-white flex items-center gap-2"><PlugZap className="w-4 h-4 text-cyan-500" />{c.displayName}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === 'DISABLED' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>{c.status}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{c.connectorType} v{c.connectorVersion} · {c.hasSecret ? 'secret set' : 'no secret'}</div>
                <div className="text-[11px] text-slate-400 mt-1">Last sync: {c.lastSuccessfulSyncAt ? new Date(c.lastSuccessfulSyncAt).toLocaleString() : '—'}</div>
              </button>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2">
            {!sel ? (
              <div className="h-full flex items-center justify-center text-slate-400 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl p-12" data-testid="no-connection-selected">Select a connection to manage mappings & sync</div>
            ) : (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5 space-y-5" data-testid="connection-detail">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">{sel.displayName}</h2>
                  <div className="flex gap-2">
                    <button onClick={testConn} className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg" data-testid="test-connection-button"><ShieldCheck className="w-4 h-4" />Test</button>
                    <button onClick={discover} className="flex items-center gap-1.5 text-sm px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg" data-testid="discover-button"><Link2 className="w-4 h-4" />Discover</button>
                    <button onClick={() => del(sel)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg" data-testid="delete-connection-button"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </div>
                </div>

                {sel.connectorType === 'p6' && (
                  <div className="rounded-xl border border-cyan-200 dark:border-cyan-900/50 bg-cyan-50/50 dark:bg-cyan-950/20 p-4 space-y-3" data-testid="p6-upload-panel">
                    <div className="flex items-center gap-2">
                      <FileUp className="w-5 h-5 text-cyan-600" />
                      <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Primavera P6 — XER Import Preview</h3>
                      <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid="p6-preview-only-badge">PREVIEW ONLY · NO APPLY</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Upload a P6 <code>.xer</code> export to preview a governed diff against the RVX active schedule. Nothing is written to authoritative records in this phase.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <select value={p6ProjectId} onChange={(e) => setP6ProjectId(e.target.value)} className="text-sm px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg" data-testid="p6-project-select">
                        <option value="">Diff against RVX project…</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <input type="file" accept=".xer" onChange={(e) => setP6File(e.target.files?.[0] || null)} className="text-sm px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-cyan-600 file:text-white" data-testid="p6-file-input" />
                      <button onClick={uploadP6Preview} disabled={busy} className="flex items-center justify-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 disabled:opacity-50 text-sm" data-testid="p6-upload-preview-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Upload & Preview</button>
                    </div>

                    {p6Preview?.needsProjectSelection && (
                      <div className="text-xs text-amber-700 dark:text-amber-300" data-testid="p6-multi-project">This XER contains multiple projects: {p6Preview.projects.map((p) => p.name).join(', ')}. Multi-project selection UI arrives in 10B-2.</div>
                    )}

                    {p6Preview && !p6Preview.needsProjectSelection && (
                      <div className="space-y-3" data-testid="p6-preview">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                          <span data-testid="p6-file-meta">File: <span className="font-mono">{p6Preview.fileName}</span></span>
                          <span>P6 Data Date: <b className="text-slate-700 dark:text-slate-200">{fmt(p6Preview.dataDate)}</b></span>
                          <span>RVX Schedule Data Date: <b className="text-slate-700 dark:text-slate-200">{fmt(p6Preview.rvxScheduleDataDate)}</b></span>
                          <span>Active RVX schedule: {p6Preview.hasActiveRvxSchedule ? 'yes' : 'none'}</span>
                        </div>

                        <div className="flex flex-wrap gap-2" data-testid="p6-counts">
                          {P6_STATUS_ORDER.filter((s) => (p6Preview.counts?.[s] || 0) > 0 || s === 'CREATE' || s === 'UPDATE').map((s) => (
                            <span key={s} className={`text-[11px] px-2 py-1 rounded-full font-medium ${P6_STATUS_STYLE[s]}`} data-testid={`p6-count-${s}`}>{s}: {p6Preview.counts?.[s] || 0}</span>
                          ))}
                        </div>

                        {/* Progress lanes — P6 vs RVX Reviewed Visual (BLOCKED) */}
                        {p6Preview.progressLanes?.length > 0 && (
                          <div className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden" data-testid="p6-progress-lanes">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-[11px] font-semibold text-slate-600 dark:text-slate-300"><Layers className="w-3.5 h-3.5" />Progress Lanes — kept separate (P6 % never overwrites RVX Reviewed Visual)</div>
                            <div className="max-h-40 overflow-y-auto text-[11px]">
                              <table className="w-full">
                                <thead className="text-slate-400 sticky top-0 bg-white dark:bg-slate-900"><tr>
                                  <th className="text-left px-3 py-1 font-medium">Activity</th>
                                  <th className="px-2 py-1 font-medium">P6 Physical</th>
                                  <th className="px-2 py-1 font-medium">P6 Duration</th>
                                  <th className="px-2 py-1 font-medium">P6 Units</th>
                                  <th className="px-2 py-1 font-medium">RVX Reviewed</th>
                                </tr></thead>
                                <tbody>
                                  {p6Preview.progressLanes.slice(0, 30).map((l, i) => (
                                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800" data-testid="p6-lane-row">
                                      <td className="text-left px-3 py-1 text-slate-600 dark:text-slate-300">{l.externalActivityId}</td>
                                      <td className="text-center px-2 py-1">{fmt(l.physicalPercent)}</td>
                                      <td className="text-center px-2 py-1">{fmt(l.durationPercent)}</td>
                                      <td className="text-center px-2 py-1">{l.unitsPercent === null ? 'Not available' : l.unitsPercent}</td>
                                      <td className="text-center px-2 py-1"><span className="inline-flex items-center gap-1 text-red-600"><Ban className="w-3 h-3" />{fmt(l.rvxReviewedVisual)}</span></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Change list with field-level drill-down */}
                        <div className="border border-slate-200 dark:border-slate-800 rounded-lg divide-y divide-slate-100 dark:divide-slate-800 max-h-72 overflow-y-auto" data-testid="p6-changes">
                          {p6Preview.changes.map((ch, i) => {
                            const open = expanded[i];
                            const hasFields = ch.fields?.length > 0;
                            return (
                              <div key={i} data-testid="p6-change-item">
                                <button onClick={() => hasFields && setExpanded({ ...expanded, [i]: !open })} className={`w-full flex items-center justify-between px-3 py-1.5 text-xs ${hasFields ? 'hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer' : 'cursor-default'}`}>
                                  <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 min-w-0">
                                    {hasFields ? (open ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />) : <span className="w-3 shrink-0" />}
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">{ch.entityType}</span>
                                    <span className="font-mono truncate">{ch.externalActivityId || ch.externalWbsCode}</span>
                                    <span className="text-slate-400 truncate hidden sm:inline">{ch.name}</span>
                                  </span>
                                  <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium ${P6_STATUS_STYLE[ch.status]}`}>{ch.status}</span>
                                </button>
                                {open && hasFields && (
                                  <div className="px-3 pb-2 overflow-x-auto">
                                    <table className="w-full text-[11px]" data-testid="p6-field-table">
                                      <thead className="text-slate-400"><tr>
                                        <th className="text-left py-1 font-medium">Field</th>
                                        <th className="text-left py-1 font-medium">P6 Previous</th>
                                        <th className="text-left py-1 font-medium">Incoming P6</th>
                                        <th className="text-left py-1 font-medium">RVX Current</th>
                                        <th className="text-left py-1 font-medium">Ownership</th>
                                      </tr></thead>
                                      <tbody>
                                        {ch.fields.map((f, k) => (
                                          <tr key={k} className="border-t border-slate-100 dark:border-slate-800" data-testid="p6-field-row">
                                            <td className="py-1 pr-2 font-medium text-slate-600 dark:text-slate-300">{f.field}</td>
                                            <td className="py-1 pr-2 text-slate-400">{fmt(f.p6PreviousValue)}</td>
                                            <td className="py-1 pr-2 text-slate-700 dark:text-slate-200">{fmt(f.externalValue)}</td>
                                            <td className="py-1 pr-2 text-slate-500">{fmt(f.currentRvxValue)}</td>
                                            <td className="py-1"><span className={`text-[10px] px-1.5 py-0.5 rounded ${f.fieldStatus === 'BLOCKED' ? P6_STATUS_STYLE.BLOCKED : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>{f.ownership}</span></td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                          <span data-testid="p6-relationship-summary">Relationships: {p6Preview.relationshipSummary?.total || 0} ({Object.entries(p6Preview.relationshipSummary?.byType || {}).map(([k, v]) => `${k}:${v}`).join(' ')})</span>
                          <span>Calendars: {p6Preview.calendars?.length || 0}</span>
                          {p6Preview.parserWarnings?.length > 0 && <span className="text-amber-600" data-testid="p6-parser-warnings">Parser warnings: {p6Preview.parserWarnings.length}</span>}
                        </div>
                        {/* Phase 10B-2 — Governed Apply */}
                        {!p6Result && (
                          <div className="pt-2 border-t border-slate-200 dark:border-slate-800 space-y-2" data-testid="p6-apply-controls">
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">P6 planning data will update <b>P6-owned</b> schedule fields only. RVX Reviewed Visual Progress, NCR/CAPA, safety and controls decisions are never overwritten.</p>
                            <div className="flex flex-wrap gap-2">
                              <button onClick={() => setP6Confirm(true)} disabled={busy} className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm" data-testid="p6-approve-apply-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Approve &amp; Apply</button>
                              <button onClick={() => applyP6('BASELINE')} disabled={busy} className="flex items-center gap-2 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-4 py-2 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 text-sm" data-testid="p6-import-baseline-button"><Layers className="w-4 h-4" />Import as Baseline</button>
                            </div>
                          </div>
                        )}
                        {p6Result && (
                          <div className="pt-2 border-t border-slate-200 dark:border-slate-800" data-testid="p6-apply-result">
                            <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /><span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Import {p6Result.status}</span><span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">{p6Result.applyMode}</span></div>
                            {p6Result.applyMode === 'SCHEDULE' ? (
                              <div className="flex flex-wrap gap-2 text-[11px]">
                                <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="p6-result-created">Created: {p6Result.created}</span>
                                <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700" data-testid="p6-result-updated">Updated: {p6Result.updated}</span>
                                <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="p6-result-unchanged">Unchanged: {p6Result.unchanged}</span>
                                <span className="px-2 py-1 rounded-full bg-orange-100 text-orange-700">External Missing: {p6Result.externalMissing}</span>
                                <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700">Warnings: {p6Result.warnings}</span>
                                <span className="px-2 py-1 rounded-full bg-red-100 text-red-700">Errors: {p6Result.errors}</span>
                              </div>
                            ) : (
                              <p className="text-xs text-slate-600 dark:text-slate-300">Immutable baseline created with {p6Result.activityCount} activities.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {(p6Baselines.length > 0 || p6Imports.length > 0) && (
                      <div className="pt-2 border-t border-slate-200 dark:border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {p6Baselines.length > 0 && (
                          <div data-testid="p6-baselines">
                            <h4 className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Baselines (immutable)</h4>
                            <div className="space-y-1">
                              {p6Baselines.map((b) => (
                                <div key={b.id} className="flex items-center gap-2 text-[11px]" data-testid={`p6-baseline-${b.id}`}>
                                  <span className="flex-1 truncate text-slate-600 dark:text-slate-300">{b.name} · {b.activityCount} acts · {fmt(b.dataDate)}</span>
                                  {b.selected ? <span className="text-emerald-600 font-medium">Selected</span> : <button onClick={() => selectBaseline(b.id)} className="text-cyan-600 hover:underline" data-testid={`p6-baseline-select-${b.id}`}>Select</button>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {p6Imports.length > 0 && (
                          <div data-testid="p6-import-history">
                            <h4 className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Import History</h4>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {p6Imports.map((im) => (
                                <div key={im.id} className="text-[11px] text-slate-600 dark:text-slate-300 flex items-center justify-between gap-2" data-testid={`p6-import-${im.id}`}>
                                  <span className="truncate">{new Date(im.importTimestamp).toLocaleString()} · {im.applyMode} · DD {fmt(im.dataDate)}</span>
                                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded ${im.status === 'APPLIED' ? 'bg-emerald-100 text-emerald-700' : im.status?.includes('FAIL') ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{im.status}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {extProjects.length > 0 && (
                  <div data-testid="discovered-projects">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">External projects</h3>
                    <div className="space-y-2">
                      {extProjects.map((ep) => (
                        <div key={ep.externalProjectId} className="flex items-center gap-2 text-sm">
                          <span className="flex-1 text-slate-700 dark:text-slate-200">{ep.name} <span className="text-xs text-slate-400">({ep.externalProjectId})</span></span>
                          <select defaultValue="" onChange={(e) => mapProject(ep.externalProjectId, e.target.value)} className="text-xs px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded" data-testid={`map-select-${ep.externalProjectId}`}>
                            <option value="">Map to RVX project…</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          <button onClick={() => runSync(ep.externalProjectId)} disabled={busy} className="text-xs px-2 py-1 bg-cyan-600 text-white rounded flex items-center gap-1" data-testid={`sync-${ep.externalProjectId}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}Sync</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {mappings.length > 0 && (
                  <div data-testid="project-mappings">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Confirmed mappings</h3>
                    {mappings.map((m) => <div key={m.id} className="text-sm text-slate-600 dark:text-slate-300">{m.externalProjectId} → {m.rvxProjectId} <span className="text-xs text-emerald-600">{m.status}</span></div>)}
                  </div>
                )}

                {job && (
                  <div data-testid="sync-preview">
                    <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Sync preview — {job.state}</h3>
                    <div className="flex flex-wrap gap-2 mb-3 text-xs">
                      {Object.entries(job.counts || {}).map(([k, v]) => (
                        <span key={k} className={`px-2 py-1 rounded ${k === 'BLOCKED' ? 'bg-red-100 text-red-700' : k === 'REVIEW_REQUIRED' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`} data-testid={`count-${k}`}>{k}: {v}</span>
                      ))}
                    </div>
                    <div className="max-h-52 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-lg divide-y divide-slate-100 dark:divide-slate-800">
                      {job.changes.slice(0, 50).map((ch, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid="change-item">
                          <span className="text-slate-600 dark:text-slate-300">{ch.externalId} · {ch.field}</span>
                          <span className={`flex items-center gap-1 ${ch.proposedAction === 'BLOCKED' ? 'text-red-600' : ch.proposedAction === 'REVIEW_REQUIRED' ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {ch.proposedAction === 'BLOCKED' ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}{ch.proposedAction} <span className="text-slate-400">({ch.ownership})</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    {job.state === 'PREVIEW_READY' && <button onClick={applyJob} disabled={busy} className="mt-3 flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50" data-testid="apply-sync-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply Import'}</button>}
                    <p className="text-[11px] text-slate-400 mt-2">Governed: RVX-authoritative fields (reviewed visual progress, NCR/CAPA, safety risk, ITP/PTW, AI reviews) are BLOCKED from overwrite.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="create-connection-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4"><h2 className="text-xl font-bold text-slate-900 dark:text-white">New Connection</h2><button onClick={() => setShowCreate(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Connector</label>
                <select value={form.connectorType} onChange={(e) => setForm({ ...form, connectorType: e.target.value })} className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg" data-testid="connector-type-select">
                  {catalog.map((c) => <option key={c.connectorType} value={c.connectorType}>{c.displayName}</option>)}
                </select>
              </div>
              <input type="text" placeholder="Display name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg" data-testid="conn-name-input" />
              <input type="password" placeholder="API key / secret (never stored in DB)" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value })} className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg" data-testid="conn-secret-input" />
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg">Cancel</button>
                <button type="submit" disabled={busy} className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-connection-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {p6Confirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="p6-confirm-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /><h2 className="text-lg font-bold text-slate-900 dark:text-white">Approve P6 Import</h2></div>
            <p className="text-sm text-slate-600 dark:text-slate-300">Primavera P6 schedule data will update <b>P6-owned schedule fields</b> (WBS, activities, dates, durations, status, relationships, P6 reported progress).</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">ReasonVisionX <b>Reviewed Visual Progress</b>, Quality/Safety decisions, NCR/CAPA and other governed records will <b>not</b> be overwritten. This is an approval to import planning data — not a validation of construction reality.</p>
            <div className="flex gap-3">
              <button onClick={() => setP6Confirm(false)} className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm" data-testid="p6-confirm-cancel">Cancel</button>
              <button onClick={() => applyP6('SCHEDULE')} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center justify-center gap-2" data-testid="p6-confirm-apply">Confirm &amp; Apply</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default IntegrationHub;
